import { format } from 'date-fns'
import { History, Save, Loader2, Sparkles } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { toast } from '@/components/ui/use-toast'

interface Checkpoint {
    id: string
    version: number
    wordCount: number
    createdAt: number
    type: 'auto' | 'manual'
    summary: string | null
    createdByName: string
}

interface HistoryPanelProps {
    scriptId: string
    onSelectVersion: (version: Checkpoint | null) => void // null means "Current Version"
    selectedVersionId?: string | null
}

export function HistoryPanel({ scriptId, onSelectVersion, selectedVersionId }: HistoryPanelProps) {
    const { projectId } = useParams()
    const queryClient = useQueryClient()

    const { data: versions, isLoading } = useQuery({
        queryKey: ['script-versions', scriptId],
        queryFn: async () => {
            const res = await api.get<{ data: Checkpoint[] }>(`/projects/${projectId}/scripts/${scriptId}/versions`)
            return res.data?.data || []
        },
        refetchInterval: 10000 // Refresh every 10s to see auto-saves
    })

    const saveMutation = useMutation({
        mutationFn: async () => {
            await api.post(`/projects/${projectId}/scripts/${scriptId}/versions`, {})
        },
        onSuccess: () => {
            toast({ title: "Checkpoint saved", description: "A new version has been created manually." })
            queryClient.invalidateQueries({ queryKey: ['script-versions', scriptId] })
        },
        onError: () => {
            toast({ title: "Save failed", description: "Could not create checkpoint.", variant: "destructive" })
        }
    })

    return (
        <div className="flex flex-col h-full border-l bg-background/50 backdrop-blur-sm w-80">
            <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <History className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-semibold text-sm">Version History</h3>
                </div>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => saveMutation.mutate()}
                                disabled={saveMutation.isPending}
                            >
                                {saveMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4" />
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Create Manual Checkpoint</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>

            <ScrollArea className="flex-1">
                <div className="p-4 space-y-4">
                    {/* Current Version Entry */}
                    <div
                        className={cn(
                            "p-3 rounded-lg border transition-colors cursor-pointer hover:bg-accent/50",
                            !selectedVersionId ? "bg-accent border-primary" : "border-transparent bg-muted/30"
                        )}
                        onClick={() => onSelectVersion(null)}
                    >
                        <div className="flex justify-between items-start mb-1">
                            <span className="font-medium text-sm">Current Version</span>
                            <Badge variant="outline" className="text-[10px] h-5">Now</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Live editing session
                        </p>
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center p-4">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {versions?.map((version) => (
                                <div
                                    key={version.id}
                                    className={cn(
                                        "p-3 rounded-lg border transition-all cursor-pointer group relative",
                                        selectedVersionId === version.id
                                            ? "bg-accent border-primary shadow-sm"
                                            : "border-border hover:border-muted-foreground/30 hover:bg-accent/30"
                                    )}
                                    onClick={() => onSelectVersion(version)}
                                >
                                    <div className="flex justify-between items-start mb-1.5">
                                        <div className="flex items-center gap-2">
                                            <Badge
                                                variant={version.type === 'manual' ? 'default' : 'secondary'}
                                                className={cn(
                                                    "text-[10px] h-5 px-1.5",
                                                    version.type === 'auto' && "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20"
                                                )}
                                            >
                                                {version.type === 'manual' ? 'Saved' : 'Auto'}
                                            </Badge>
                                            <span className="text-xs text-muted-foreground font-mono">
                                                v{version.version}
                                            </span>
                                        </div>
                                        <span className="text-[10px] text-muted-foreground">
                                            {format(version.createdAt, 'h:mm a')}
                                        </span>
                                    </div>

                                    {/* AI Summary */}
                                    {version.summary && (
                                        <div className="mb-2 p-2 bg-muted/50 rounded text-xs text-muted-foreground/90 leading-relaxed border border-border/50">
                                            <div className="flex items-center gap-1.5 mb-1 text-[10px] text-primary/70 uppercase font-bold tracking-wider">
                                                <Sparkles className="h-2.5 w-2.5" />
                                                Summary
                                            </div>
                                            {version.summary}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                                        <span>{version.createdByName}</span>
                                        <span>{format(version.createdAt, 'MMM d')}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </ScrollArea>
        </div>
    )
}
